/* Lighting for the axis box's three planes - the floor and the two walls.
 *
 * The box was the last thing on the canvas that did not know there was a light. The surface is
 * shaded per pixel by `lighting`, the marks are lit against a sphere normal, and the planes behind
 * both were one flat wash of ink over the panel. Three planes at one tone read as a folded sheet of
 * paper; the same three lit by the rig read as the inside of a box.
 *
 * Three cues, answering different questions:
 *
 * - Which plane is this? A plane has a constant normal, so the rig gives it one value. The floor
 *   faces up into the sky fill and the walls stand edge-on to it, and `KEY_LIGHT` is in view axes,
 *   so whichever wall the light favours lifts as the camera turns.
 * - Where is the corner? {@link CORNER_INK} - ambient occlusion, radial from the point where the
 *   two walls meet the floor. This is the one that makes the box read as an interior rather than
 *   as three panels at three tones, and it was missing from the first version of this file: a
 *   value ramp along one axis of a face cannot say where a crease is, because occlusion falls off
 *   from a *point*.
 * - How far into the box is this part of it? A wash with depth, applied after the shading, as
 *   atmosphere sits between the eye and a surface rather than being a property of it. Deliberately
 *   the weakest of the three now, since the corner shadow says the same thing better.
 *
 * The planes also keep the light's colour, where the marks average it away. That is not an
 * inconsistency: a mark's fill is the datum a reader matches against the legend, so moving its hue
 * breaks the encoding, and a wall carries no datum. Only the floor has a cast to keep - it faces up
 * into the blue sky fill - because a vertical face clamps that light to zero and is left with the
 * white key alone.
 *
 * Both are expressed as a fraction of ink over the panel rather than as a multiplier on the panel,
 * and that is the one decision here worth defending, because the obvious thing is to call
 * `applyShading` the way the surface does. Measured, a multiplier collapses on the dark theme: it
 * scales the panel toward black, and a panel that is already near-black has nowhere to go, so the
 * floor and the walls landed *two* value steps apart where the light theme got 25. The rig's `fog`
 * fails the same way in mirror image - washing a near-white panel toward a near-white fog colour
 * moved a wall by one step across the whole depth of the box, against 21 on the dark panel.
 *
 * Ink is what this file already reaches for, for the reason `TONE` gives: ink is whatever contrasts
 * with the surface, so more of it is darker in the light theme and lighter in the dark one. One
 * expression, both themes, each in its own direction.
 */

import { toCssColor, type Rgb } from './colorScale';
import { DEFAULT_LIGHTING, getShadingTerms, type Vector3 } from './lighting';
import type { CameraBasis } from '../geometry/projection';

/**
 * The ink a plane carries at its most lit, and the span the rig may add on top.
 *
 * The span is the cue and the cost in one number. The wall grid is `inkColor` at `GRID_INK_TONE`
 * over the wall, so the contrast that rules a wall is proportional to how much panel is left under
 * it: a plane at ink fraction `f` keeps `1 - f` of the separation the grid had. At the span below,
 * the worst-lit wall at the furthest corner keeps over four fifths of it.
 */
const PLANE_MIN_INK = 0.02;
const PLANE_LIT_SPAN = 0.11;

/**
 * How much ink the furthest point of the box adds, on top of whichever plane it is on.
 *
 * Independent of the shading rather than sharing its budget: the shading says which plane this is
 * and this says how deep into the box a point on it sits. Two questions that happen to land on the
 * same channel.
 *
 * Small, because {@link CORNER_INK} says the same thing better. Both are about
 * depth, and a ramp spread evenly across a whole face is the weaker way to say it - the corner
 * shadow puts the same ink where the eye reads it as a shape.
 */
const PLANE_DEPTH_INK = 0.03;

/** The most ink any plane can reach, which is what the box's thickness has to stay clear of. */
export const PLANE_MAX_INK = PLANE_MIN_INK + PLANE_LIT_SPAN + PLANE_DEPTH_INK;

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/** `#rgb`, `#rrggbb`, `rgb()` and `rgba()`, which is everything the design tokens resolve to. */
export const parseColor = (css: string): Rgb | null => {
  const trimmed = css.trim();

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(trimmed);
  if (hex) {
    const digits = hex[1];
    const pairs =
      digits.length === 3
        ? [...digits].map(digit => digit + digit)
        : [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)];
    const [r, g, b] = pairs.map(pair => parseInt(pair, 16));
    return [r, g, b];
  }

  const rgb = /^rgba?\(([^)]+)\)$/i.exec(trimmed);
  if (rgb) {
    const parts = rgb[1]
      .split(/[\s,/]+/)
      .filter(part => part !== '')
      .map(Number);
    if (parts.length < 3 || parts.slice(0, 3).some(Number.isNaN)) return null;
    return [parts[0], parts[1], parts[2]];
  }

  return null;
};

/**
 * How lit a plane facing this way is, 0 to 1, against the rig's own range.
 *
 * Normalised out of the rig rather than read off it: `intensity` runs from the model's `ambient` to
 * 1, and what this needs is where in that span the plane sits, so the ink below can be a fraction
 * the box controls. Taking the rig's absolute number instead is what tied the result to the panel's
 * own lightness and broke the dark theme.
 *
 * The directions are the shipped ones and only the directions are used, so the box agrees with the
 * surface and the marks about where the light is.
 */
export const toPlaneLitness = (normal: Vector3, basis: CameraBasis): number => {
  const { intensity } = getShadingTerms(normal, basis, DEFAULT_LIGHTING);
  const mean = (intensity[0] + intensity[1] + intensity[2]) / 3;

  return clamp01(
    (mean - DEFAULT_LIGHTING.ambient) / (1 - DEFAULT_LIGHTING.ambient)
  );
};

/**
 * The cast the lights leave on a plane, as a per-channel multiplier around 1.
 *
 * Kept apart from {@link toPlaneLitness} because the two answer different questions and only one of
 * them is about brightness. Averaging the rig's channels - which is what the mark shading does, and
 * correctly, since a mark's fill is the datum a reader matches against the legend - throws the
 * light's colour away. A wall carries no datum, so it can afford the colour, and the floor has a
 * real one to carry: `SKY_FILL_LIGHT` is blue and overhead, so a floor facing up into it measures a
 * channel spread of 0.053, or thirteen value steps of cool cast on the light theme.
 *
 * The walls get nothing from this and that is not a bug - the sky fill's lambert on a vertical face
 * clamps to zero, so the only light reaching a wall is the white key. There is no hue to recover
 * there without tinting the key, which would move the surface chart and the marks with it.
 */
export const toPlaneCast = (normal: Vector3, basis: CameraBasis): Rgb => {
  const { intensity } = getShadingTerms(normal, basis, DEFAULT_LIGHTING);
  const mean = (intensity[0] + intensity[1] + intensity[2]) / 3 || 1;

  return [intensity[0] / mean, intensity[1] / mean, intensity[2] / mean];
};

/**
 * The ink a plane carries, given which way it faces and how near this part of it is.
 *
 * `nearness` is 0 at the furthest corner of the box and 1 at the nearest, measured over the whole
 * box rather than per face - a wall stands at the back and covers less depth than the floor, and
 * giving each face its own full ramp would make them disagree about where "far" is at the edge
 * they share.
 */
export const toPlaneInk = (
  normal: Vector3,
  basis: CameraBasis,
  nearness: number
): number =>
  PLANE_MIN_INK +
  PLANE_LIT_SPAN * (1 - toPlaneLitness(normal, basis)) +
  PLANE_DEPTH_INK * clamp01(1 - nearness);

/** Panel with `ink` mixed over it at the given fraction. */
export const toInkedColor = (panel: Rgb, ink: Rgb, fraction: number): Rgb => [
  Math.round(panel[0] + (ink[0] - panel[0]) * fraction),
  Math.round(panel[1] + (ink[1] - panel[1]) * fraction),
  Math.round(panel[2] + (ink[2] - panel[2]) * fraction),
];

const CHANNEL_MAX = 255;

/**
 * The plane's colour with the light's cast applied.
 *
 * After the ink rather than before it: the cast belongs to the light falling on the surface, and
 * the ink is standing in for how much of that light there is. Held back to `CAST_STRENGTH` because
 * the rig's own tint is tuned against a surface whose colour is a ramp readout, where it may only
 * shade; here it is free to be seen, which is not the same as being free to be loud.
 */
const CAST_STRENGTH = 0.6;

const withCast = (colour: Rgb, cast: Rgb): Rgb =>
  colour.map((channel, index) =>
    Math.round(
      Math.max(
        0,
        Math.min(CHANNEL_MAX, channel * (1 + (cast[index] - 1) * CAST_STRENGTH))
      )
    )
  ) as unknown as Rgb;

/**
 * How much ink the box's inside corner carries, over and above its plane's own shading.
 *
 * Ambient occlusion, and the cue the rest of this file was missing. A plane's tone says which way
 * it faces and the depth wash says how far away it is, and neither says anything about the corner
 * two planes make - which is the one place a reader can see that this is the inside of a box
 * rather than three panels at three tones. Light arriving from everywhere reaches a crease least,
 * so a crease is darker; that is what makes a rendered interior read as lit, and it is what a
 * value ramp along one axis cannot express, because occlusion falls off from a *point* rather than
 * across a face.
 *
 * Strong relative to the other two terms on purpose. It is concentrated near one corner rather
 * than spread over a whole face, so the same number of value steps buys a far more visible cue -
 * and it fades to nothing before it reaches the open edges, where the grid labels are read.
 */
const CORNER_INK = 0.16;

/**
 * How far the corner's shadow reaches, as a fraction of the box's projected size.
 *
 * Short enough that the plot's own area is barely touched and the far corner is unmistakable.
 */
export const CORNER_REACH = 0.55;

/**
 * The darkest any point on a plane can get, corner shadow included.
 *
 * Separate from {@link PLANE_MAX_INK}, which is what the box's thickness has to stay clear of: the
 * shadow is a second fill over the plane's own tone, so the grid meets their sum rather than
 * either one. Easy to get wrong in exactly one direction - the contrast test measured
 * `PLANE_MAX_INK` alone until the shadow arrived, and would have signed off on a corner half
 * again as dark as the number it was asked about.
 */
export const PLANE_DARKEST_INK = PLANE_MAX_INK + CORNER_INK;

/** The stops of the corner shadow, from the corner outward. `null` where it is off. */
export const toCornerStops = (
  ink: Rgb
): { offset: number; color: string }[] => {
  /* Squared falloff rather than linear: occlusion drops off fast and a linear ramp reads as a
     cone of paint rather than as a shadow. Sampled rather than expressed as two stops for the
     same reason the depth wash is - a gradient interpolates linearly between whatever it is
     given. */
  const [r, g, b] = ink;

  return [0, 0.25, 0.5, 0.75, 1].map(offset => ({
    offset,
    color: `rgba(${r}, ${g}, ${b}, ${(CORNER_INK * (1 - offset) ** 2).toFixed(4)})`,
  }));
};

export type PanelGradientStop = {
  /** 0 at the gradient's start, 1 at its end. */
  offset: number;
  color: string;
};

/**
 * The stops for one plane, from its furthest corner to its nearest.
 *
 * Three rather than two: the wash is linear in `nearness` but the projection is not, so the middle
 * of the screen-space gradient is not the middle of the depth range. One stop between the ends is
 * enough to keep the wash following the geometry instead of the screen.
 */
export const toPanelGradient = (
  panel: Rgb,
  ink: Rgb,
  normal: Vector3,
  basis: CameraBasis,
  [farNearness, nearNearness]: [number, number]
): PanelGradientStop[] =>
  [0, 0.5, 1].map(offset => {
    const cast = toPlaneCast(normal, basis);
    return {
      offset,
      color: toCssColor(
        withCast(
          toInkedColor(
            panel,
            ink,
            toPlaneInk(
              normal,
              basis,
              farNearness + (nearNearness - farNearness) * offset
            )
          ),
          cast
        )
      ),
    };
  });
